import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  Checkbox,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { eopdAPI } from "../../../../../services/e-opd.api";
import { toast } from "react-toastify";
import "../OpdStepper.css";

const PORTAL_CARDS = [
  { title: "NCD PORTAL", sub: "BP / HTN", bg: "#2F80ED" },
  { title: "JANANI PORTAL", sub: "Maternal & Child Health", bg: "#009688" },
  { title: "COMMUNICABLE DISEASE", sub: "Nikshay / Nikusth / IHIP", bg: "#E28704" },
  { title: "OTHERS", sub: "Sickle Cell / I-MIS / S-HVR", bg: "#7CB342" },
];

const SCREENING_GROUPS = [
  {
    title: "A. NCD",
    items: [
      { label: "Diabetes - RBS", checked: false },
      { label: "Hypertension - Forearm BP", checked: false },
      { label: "Oral Ca - Visual exam", checked: false },
      { label: "Breast Ca - Palpation", checked: false },
      { label: "Cervical Ca - VIA", checked: false },
      { label: "NAFLD - Waist/Hip Ratio", checked: false },
      { label: "COPD - Spirometer", checked: false },
      { label: "CKD", checked: false },
      { label: "STEMI", checked: false },
      { label: "Stroke - Coherence", checked: false },
    ],
  },
  {
    title: "B. ORAL",
    items: [
      { label: "Ca / Lesions / Leukoplakia", checked: false },
      { label: "Teeth - Cavities/Staining/Gums", checked: false },
    ],
  },
  {
    title: "C. ENT",
    items: [
      { label: "Hearing Loss", checked: false },
      { label: "Throat - Redness/Lump/Lesion", checked: false },
      { label: "Nose - Smell/Lesions", checked: false },
    ],
  },
  {
    title: "D. EYE",
    items: [
      { label: "Refractive Errors", checked: false },
      { label: "Redness", checked: false },
      { label: "Vision", checked: false },
    ],
  },
  {
    title: "E. MENTAL HEALTH",
    items: [
      { label: "PHQ & GAD", checked: false },
      { label: "Substance Use", checked: false },
    ],
  },
  {
    title: "F. ELDERLY",
    items: [
      { label: "Mobility Range", checked: false },
    ],
  },
  {
    title: "G. SICKLE CELL",
    items: [
      { label: "POC Screening (where applicable)", checked: false },
    ],
  },
];

const inputFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px !important",
    backgroundColor: "#F9FAFB",
    transition: "all 0.2s ease",
    fontSize: "13.5px",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&.Mui-disabled": {
      backgroundColor: "#F9FAFB",
      "& fieldset": { borderColor: "#E5E7EB" },
    },
  },
};

function DisabledCheckbox({ label, checked }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: checked ? "1.5px solid #1E88E5" : "1.5px solid #E5E7EB",
        borderRadius: "8px",
        backgroundColor: checked ? "#F4F9FF" : "#FFFFFF",
        opacity: checked ? 1 : 0.65,
        transition: "all 0.2s ease",
      }}
    >
      <Checkbox
        checked={checked}
        disabled
        size="small"
        sx={{
          color: checked ? "#1E88E5 !important" : "#9CA3AF !important",
          "&.Mui-checked": {
            color: "#1E88E5 !important",
          },
          p: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "13.5px",
          fontWeight: 600,
          color: checked ? "#1E88E5" : "#4A5670",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function Step4ScreeningRouting({ prevStep }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({
    portal: true,
    screening: true,
    govHcf: true,
    pmjay: true,
    followup: true,
  });

  const handleToggle = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const displayId = localStorage.getItem("currentOpdDisplayId") || "";
      const savedPayload = localStorage.getItem("opdDrugsPayload");
      
      let payload = {
        display_id: displayId,
        drugs: []
      };
      
      if (savedPayload) {
        payload = JSON.parse(savedPayload);
      }
      
      const response = await eopdAPI.saveOpdStep(7, payload);
      if (response && response.success) {
        toast.success(response.message || "OPD visit completed successfully!");
        localStorage.removeItem("beneficiaryDetails");
        localStorage.removeItem("orignalBeneficiaryData");
        localStorage.removeItem("opdDrugsPayload");
        navigate(`/e-opd/visit-summary/${displayId}`);
      } else {
        toast.error(response.message || "Failed to complete visit.");
      }
    } catch (error) {
      console.error(error);
      const errMsg = error.response?.data?.message || error.message || "Failed to complete visit.";
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderReferralFields = () => (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 4 }}>
        <Box className="opd-input-group">
          <Typography className="opd-field-label">HFR (Optional)</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter HFR"
            disabled
            sx={inputFieldStyle}
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <Box className="opd-input-group">
          <Typography className="opd-field-label">HPR (Optional)</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter HPR"
            disabled
            sx={inputFieldStyle}
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <Box className="opd-input-group">
          <Typography className="opd-field-label">Contact No. (Optional)</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Contact no."
            disabled
            sx={inputFieldStyle}
          />
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={submitting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Typography className="opd-step-title">
        Screening, Routing, Referral & Follow-Up
      </Typography>

      <Stack spacing={3}>
        {/* Auto Direct to Portal Accordion */}
        <Accordion
          expanded={expanded.portal}
          onChange={handleToggle("portal")}
          className={`opd-accordion ${expanded.portal ? "opd-accordion-expanded" : ""}`}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
            <Box>
              <Typography className="opd-accordion-title">Auto Direct to Portal</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className="opd-accordion-details">
            <Grid container spacing={2}>
              {PORTAL_CARDS.map((card, i) => (
                <Grid size={{ xs: 12, sm: 6 }} key={i}>
                  <Card
                    sx={{
                      background: card.bg,
                      color: "#FFFFFF",
                      borderRadius: "8px",
                      p: 2.5,
                      minHeight: "80px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      boxShadow: "none",
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: "14px", letterSpacing: "0.5px" }}>
                      {card.title}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", opacity: 0.9, mt: 0.5 }}>
                      {card.sub}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Screening Accordion */}
        <Accordion
          expanded={expanded.screening}
          onChange={handleToggle("screening")}
          className={`opd-accordion ${expanded.screening ? "opd-accordion-expanded" : ""}`}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
            <Box>
              <Typography className="opd-accordion-title">Screening (Opportunistic)</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className="opd-accordion-details">
            <Stack spacing={3}>
              {SCREENING_GROUPS.map((group, idx) => (
                <Box key={idx}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1E88E5", mb: 1.5 }}>
                    {group.title}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {group.items.map((item, i) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={i}>
                        <DisabledCheckbox label={item.label} checked={item.checked} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Referral Section */}
        <Box>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111C33", mt: 1, mb: 2 }}>
            Referral
          </Typography>
          <Stack spacing={2}>
            <Accordion
              expanded={expanded.govHcf}
              onChange={handleToggle("govHcf")}
              className={`opd-accordion ${expanded.govHcf ? "opd-accordion-expanded" : ""}`}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
                <Box>
                  <Typography className="opd-accordion-title">A) Government HCF</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="opd-accordion-details">
                {renderReferralFields()}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded.pmjay}
              onChange={handleToggle("pmjay")}
              className={`opd-accordion ${expanded.pmjay ? "opd-accordion-expanded" : ""}`}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
                <Box>
                  <Typography className="opd-accordion-title">B) PMJAY Empanelled Hospital (if eligible)</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="opd-accordion-details">
                {renderReferralFields()}
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Box>

        {/* Follow-up Section */}
        <Box>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111C33", mt: 1, mb: 2 }}>
            Follow-up
          </Typography>
          <Accordion
            expanded={expanded.followup}
            onChange={handleToggle("followup")}
            className={`opd-accordion ${expanded.followup ? "opd-accordion-expanded" : ""}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
              <Box>
                <Typography className="opd-accordion-title">Follow-up</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className="opd-accordion-details">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box className="opd-input-group">
                    <Typography className="opd-field-label">Next Visit Date (Optional)</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value="13-05-2028"
                      disabled
                      sx={inputFieldStyle}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarTodayIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent={prevStep ? "space-between" : "flex-end"}
        alignItems="center"
        sx={{ mt: 3 }}
      >
        {prevStep && (
          <Button
            type="button"
            variant="outlined"
            className="opd-back-btn"
            onClick={prevStep}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={submitting}
          className="opd-next-btn"
          sx={{
            px: 4,
            py: 1.2,
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "8px",
          }}
        >
          {submitting ? "Submitting..." : "Submit"}
        </Button>
      </Stack>
    </Box>
  );
}
